import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, FolderOpen, Visibility } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import api from '../../utils/api';
import { Project, ProjectStatus } from '../../types';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import PageHeader from '../../components/Common/PageHeader';
import DataTableFilter from '../../components/Common/DataTableFilter';
import AddProjectDialog from '../../components/Projects/AddProjectDialog';
import EditProjectDialog from '../../components/Projects/EditProjectDialog';
import ProjectDetailsDialog from '../../components/Projects/ProjectDetailsDialog';
import ToastNotification from '../../utils/toastNotification';
import { useDataFilter } from '../../hooks/useDataFilter';

const ProjectsPage: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean; project: Project | null }>({
    open: false,
    project: null,
  });
  const [detailsDialog, setDetailsDialog] = useState<{ open: boolean; project: Project | null }>({
    open: false,
    project: null,
  });

  // Filters
  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    status: '',
    constructionStatus: '',
    governorate: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const projectsRes = await api.get('/projects');
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      ToastNotification.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Filter projects
  const filteredProjects = useDataFilter(
    projects,
    searchValue,
    filterValues,
    {
      searchFields: ['name', 'description', 'ownerName', 'city', 'town'],
      filterFields: {
        status: (item, value) => !value || item.status === value,
        constructionStatus: (item, value) => !value || item.constructionStatus === value,
        governorate: (item, value) => !value || item.governorate === value,
      },
    }
  );

  // Extract unique governorates for filter
  const governorateOptions = useMemo(() => {
    const uniqueGovernorates = Array.from(
      new Set(projects.map((p) => p.governorate).filter(Boolean))
    );
    return uniqueGovernorates;
  }, [projects]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleDelete = async () => {
    if (!deleteDialog.id) return;

    try {
      await api.delete(`/projects/${deleteDialog.id}`);
      ToastNotification.success('Project deleted successfully');
      loadData();
      setDeleteDialog({ open: false, id: null });
    } catch (error: any) {
      console.error('Error deleting project:', error);
      ToastNotification.error(error.response?.data?.message || 'Failed to delete project');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PLANNING':
        return 'info';
      case 'UNDER_CONSTRUCTION':
        return 'warning';
      case 'COMPLETED':
        return 'success';
      case 'ON_HOLD':
        return 'default';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const projectColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Project Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'ownerName',
      headerName: 'Owner',
      width: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.row.status.replace(/_/g, ' ')}
          size="small"
          color={getStatusColor(params.row.status) as any}
        />
      ),
    },
    {
      field: 'constructionStatus',
      headerName: 'Construction',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.row.constructionStatus === 'COMPLETED' ? 'Completed' : 'In Progress'}
          size="small"
          color={params.row.constructionStatus === 'COMPLETED' ? 'success' : 'warning'}
        />
      ),
    },
    {
      field: 'completionPercentage',
      headerName: 'Completion',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={params.row.completionPercentage || 0}
              sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption">{params.row.completionPercentage || 0}%</Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'governorate',
      headerName: 'Location',
      width: 130,
      valueGetter: (params) => params.row.governorate || 'N/A',
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 120,
      valueGetter: (params) => format(new Date(params.row.startDate), 'MMM dd, yyyy'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const canModify = isSuperAdmin || params.row.createdById === user?.id;
        return (
          <Box>
            <IconButton
              size="small"
              color="info"
              onClick={() => setDetailsDialog({ open: true, project: params.row })}
            >
              <Visibility />
            </IconButton>
            {canModify && (
              <IconButton
                size="small"
                color="primary"
                onClick={() => setEditDialog({ open: true, project: params.row })}
              >
                <Edit />
              </IconButton>
            )}
            {canModify && (
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteDialog({ open: true, id: params.row.id })}
              >
                <Delete />
              </IconButton>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Projects & Activities"
        subtitle="Track building project progress and system activities"
        icon={<FolderOpen sx={{ fontSize: 'inherit', color: 'primary.main' }} />}
        breadcrumbs={[
          { label: 'Dashboard', path: '/' },
          { label: 'Projects' },
        ]}
        actions={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialog(true)}
            size="large"
            sx={{
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            New Project
          </Button>
        }
      />

      <Paper
        sx={{
          p: 3,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Typography variant="h6" gutterBottom fontWeight={700} sx={{ mb: 2 }}>
          📊 Active Projects
        </Typography>

        {/* Filters */}
        <DataTableFilter
          searchPlaceholder="Search projects..."
          onSearchChange={handleSearchChange}
          filters={[
            {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: Object.values(ProjectStatus).map((status) => ({
                value: status,
                label: status.replace(/_/g, ' '),
              })),
            },
            {
              key: 'constructionStatus',
              label: 'Construction Status',
              type: 'select',
              options: [
                { value: 'UNDER_CONSTRUCTION', label: 'Under Construction' },
                { value: 'COMPLETED', label: 'Completed' },
              ],
            },
            {
              key: 'governorate',
              label: 'Governorate',
              type: 'select',
              options: governorateOptions.map((gov) => ({ value: gov as string, label: gov as string })),
            },
          ]}
          onFiltersChange={(values) => setFilterValues(values)}
          initialFilterValues={filterValues}
        />

        <DataGrid
          rows={filteredProjects}
          columns={projectColumns}
          loading={loading}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            minHeight: 500,
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#f8f9fa',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9f9f9' },
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'auto',
            },
          }}
        />
      </Paper>

      {/* Dialogs */}
      <AddProjectDialog
        open={addDialog}
        onClose={() => setAddDialog(false)}
        onSuccess={loadData}
      />

      <EditProjectDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, project: null })}
        onSuccess={loadData}
        project={editDialog.project}
      />

      <ProjectDetailsDialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, project: null })}
        project={detailsDialog.project}
      />

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog({ open: false, id: null })}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default ProjectsPage;
